import { Options } from 'vue-class-component';
import CommandWizardStep from '../CommandWizardStep';
import {
  getObjectIconFromType,
  getObjectDescLabel,
  getDeviceIconFromSettings,
  getDeviceIconName,
} from '@/core/utils/utils';
import { SET_APP_LOADING } from '@/core/store/action-types';
import deviceSettingsClient from '@/core/services/device-settings-service';
import { IDefaultDevicesSettingsDTO } from '@/../client-generator/generated-clients/device-settings';
import { HANDLE_REFRESH_PLANT } from '@/core/utils/emitter-events';
import ShutterCommandSettings from '@/views/components/DeviceSettings/ShutterCommandSettings/ShutterCommandSettings.vue';
import LightsCommandSettings from '@/views/components/DeviceSettings/LightsCommandSettings/LightsCommandSettings.vue';
import LocksCommandSettings from '@/views/components/DeviceSettings/LocksCommandSettings/LocksCommandSettings.vue';
import CommandIconComponent from '@/views/components/CommandIcon/CommandIconComponent.vue';
import { AggregateCommandDTO } from '@/../client-generator/generated-clients/plants';

@Options({
  name: 'CommandWizardStepOverview',

  components: {
    ShutterCommandSettings,
    LocksCommandSettings,
    LightsCommandSettings,
    CommandIconComponent,
  },
})
export default class CommandWizardStepOverview extends CommandWizardStep {
  getObjectIconFromType = getObjectIconFromType;
  getObjectDescLabel = getObjectDescLabel;
  getDeviceIconFromSettings = getDeviceIconFromSettings;
  getDeviceIconName = getDeviceIconName;

  showRightPanel = false;
  activeSettingsComponent = '';
  currentDefaultSetting: IDefaultDevicesSettingsDTO | null = null;
  currentDeviceName: string | null = null;

  async mounted() {
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const response = await deviceSettingsClient.aggregateCommandsDefault(
        this.commandGroup.id ?? '',
        AggregateCommandDTO.fromJS(this.commandGroup)
      );

      for (const deviceSetting of response) {
        this.parseShutterActuatorSettings(deviceSetting);
      }

      this.$emit('update:defaultSettings', response);
    } catch (error) {
      console.log(error);
      let messageError = '';

      switch (error.status) {
        case 409:
          this.$emitter.emit(HANDLE_REFRESH_PLANT);
          this.$router.push({
            name: 'page-plant-dashboard',
            params: this.$route.params,
          });
          break;
        default:
          messageError = this.$t('apiErrors.serverError').toString();
      }
      if (messageError) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('general.operationError'),
          closable: true,
          life: 3000,
        });
      }
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  hasDeviceSettingsButtonVisible(setting: IDefaultDevicesSettingsDTO) {
    return (
      setting?.shuttersCommandSettings != null ||
      setting?.lightsCommandSettings != null ||
      setting?.lockCommandSettings != null
    );
  }

  hasDeviceCommandIconButtonVisible(setting: IDefaultDevicesSettingsDTO) {
    return setting?.hasChangeIconButtonVisible;
  }

  openAdvancedSettings() {
    this.showRightPanel = true;
  }

  showDeviceSettings(setting: IDefaultDevicesSettingsDTO) {
    if (setting?.shuttersCommandSettings != null) {
      this.activeSettingsComponent = 'ShutterCommandSettings';
    } else if (setting?.lightsCommandSettings != null) {
      this.activeSettingsComponent = 'LightsCommandSettings';
    } else if (setting?.lockCommandSettings != null) {
      this.activeSettingsComponent = 'LocksCommandSettings';
    } else return;

    this.currentDefaultSetting = setting;
    this.showRightPanel = true;
    this.currentDeviceName = this.getDeviceName(setting.deviceUid as string);
  }

  parseShutterActuatorSettings(defaultSettings: IDefaultDevicesSettingsDTO) {
    if (!defaultSettings.shuttersActuatorSettings) return;
    defaultSettings.shuttersActuatorSettings.stopPulseDuration =
      defaultSettings.shuttersActuatorSettings.stopPulseDuration! * 100;
    defaultSettings.shuttersActuatorSettings.upDownPulseDuration =
      defaultSettings.shuttersActuatorSettings.upDownPulseDuration! * 100;
    defaultSettings.shuttersActuatorSettings.presetValue =
      defaultSettings.shuttersActuatorSettings.presetValue! * 10;
    defaultSettings.shuttersActuatorSettings.delayBetweenDoorsTime =
      defaultSettings.shuttersActuatorSettings.delayBetweenDoorsTime! * 1000;
  }

  showDeviceCommandIcon(setting: IDefaultDevicesSettingsDTO) {
    this.currentDefaultSetting = setting;
    this.showRightPanel = true;
    this.currentDeviceName = this.getDeviceName(setting.deviceUid as string);
    this.activeSettingsComponent = 'CommandIconComponent';
  }

  hideRightPanel() {
    this.currentDefaultSetting = null;
    this.showRightPanel = false;
    this.currentDeviceName = '';
  }

  getDeviceName(deviceUuid: string) {
    const device = this.commandGroup.devices?.find((k) => k.uid === deviceUuid);
    if (!device) return '';
    return `${device.model} - ID ${device.id}`;
  }
}
